import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import { postForgotPassword } from '@/redux/api/auth';

import { REHYDRATE } from 'redux-persist/constants';

/* Action Types */
export const FORGOT_PASSWORD_FAIL = 'la/ui/forgotPassword/FAIL';
export const FORGOT_PASSWORD_REQUEST = 'la/ui/forgotPassword/REQUEST';
export const FORGOT_PASSWORD_SUCCESS = 'la/ui/forgotPassword/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    submitted: false,
    success: false,
    timestamps: {},
};

export type State = typeof DEFAULT_STATE & {
    errorMessage: string | null | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case REHYDRATE:
            if (Object.prototype.hasOwnProperty.call(action.payload, 'forgotPassword')) {
                const { timestamps } = action.payload.forgotPassword;
                return { ...state, timestamps };
            }

            return state;
        case FORGOT_PASSWORD_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case FORGOT_PASSWORD_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
                timestamps: {
                    [action.payload.emailAddress]: action.meta.actionTime,
                },
            };
        case FORGOT_PASSWORD_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: action.payload.message,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.forgotPassword;
const idSelector = (state: GlobalState, id) => id;
const getForgotPasswordTimestamps = createSelector(stateSelector, (state) => state.timestamps);

export const getUiForgotPassword = createSelector(stateSelector, (state) => state);

const getIsAtLeast5MinutesSinceLastRequest = createSelector(
    [getForgotPasswordTimestamps, idSelector],
    (timestamps, id) => Date.now() - (timestamps[id] || 0) >= ms('5m')
);

/* ACTION CREATORS */
export const submitForgotPassword =
    (emailAddress: string, sendSMS: boolean) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);

            dispatch({
                payload: { emailAddress, sendSMS },
                type: FORGOT_PASSWORD_REQUEST,
            });

            if (!getIsAtLeast5MinutesSinceLastRequest(state, emailAddress)) {
                return dispatch({
                    error: true,
                    meta: { emailAddress, sendSMS },
                    payload: {
                        message: 'For your account security, please wait 5 min between sending reset messages.',
                    },
                    type: FORGOT_PASSWORD_FAIL,
                });
            }

            await postForgotPassword({ deployment, emailAddress, sendSMS });
            return dispatch({
                meta: { actionTime: Date.now() },
                payload: { emailAddress, sendSMS },
                type: FORGOT_PASSWORD_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { emailAddress, sendSMS },
                payload: error,
                type: FORGOT_PASSWORD_FAIL,
            });
        }
    };
